import { useEffect } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

import { getPublicConfig } from "../../api/config/getPublicConfig";
import { getMyDossiers } from "../../api/dossier/getMyDossiers";
import { PageDirectoryEntryName } from "../../api/PageDirectory";
import { qualifyNewLead } from "../../api/user/qualifyNewLead";
import PageContainer from "../../components/PageContainer";
import { isAllowedLatestDossierRedirectRoute } from "../../helpers/configuration";
import {
  getDossierStatusGroup,
  getLatestActivatedDossier,
  getLatestActiveDossier,
  getLatestDossier,
  getLatestOpenDossier,
} from "../../helpers/dossiers";
import { useAuth } from "../../providers/AuthProvider";
import { Dossier, DossierStatusGroup } from "../../types/Dossier";

const RedirectToLatestDossierPage = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const destination = params.destination as PageDirectoryEntryName;

  const status = searchParams.get("status") as DossierStatusGroup | undefined;
  const secondaryId = searchParams.get("secondaryId") as string | undefined;

  const otherParams = new URLSearchParams();

  searchParams.forEach((value, key) => {
    if (key !== "status" && key !== "secondaryId") {
      otherParams.append(key, value);
    }
  });

  const otherQueryString = otherParams.toString();

  const { getScopedPageUrl } = useAuth();

  if (!destination) {
    // TODO: improve client side error messaging
    throw new Error("destination param is missing");
  }

  const configMap = getPublicConfig();
  const dossiers = getMyDossiers();
  const { refetch: refetchDossiers } = dossiers;

  const { mutateAsync: qualifyNewLeadAsync, isLoading: isQualifyingNewLead } =
    qualifyNewLead();

  if (dossiers.isError) {
    throw dossiers.error;
  }

  if (configMap.isError) {
    throw configMap.error;
  }

  let dossier: Dossier | null = null;

  if (dossiers.data) {
    if (status === "open") {
      dossier = getLatestOpenDossier(dossiers.data);
    } else if (status === "active") {
      dossier = getLatestActiveDossier(dossiers.data);
    } else if (status === "activated") {
      dossier = getLatestActivatedDossier(dossiers.data);
    } else {
      dossier = getLatestDossier(dossiers.data);
    }
  }

  const handleQualifyNewLead = async () => {
    await qualifyNewLeadAsync();
    refetchDossiers();
  };

  const dossierStatusGroup = dossier ? getDossierStatusGroup(dossier) : null;

  const destinationIsAllowed =
    configMap.data && dossierStatusGroup
      ? isAllowedLatestDossierRedirectRoute(
          destination,
          dossierStatusGroup,
          configMap.data
        )
      : false;
  const isSuccess = dossiers.isSuccess && configMap.isSuccess;

  const shouldQualifyNewLead = status === "open" && !dossier;

  useEffect(() => {
    // Wait for all necessary data to load
    if (
      isSuccess &&
      !dossiers.isFetching &&
      shouldQualifyNewLead &&
      !isQualifyingNewLead
    ) {
      // No open dossier, so qualify a new lead
      handleQualifyNewLead();
    }
  }, [isSuccess, dossiers.isFetching, status, dossier, isQualifyingNewLead]);

  const isLoading =
    dossiers.isLoading || configMap.isLoading || isQualifyingNewLead;

  return (
    <PageContainer loading={isLoading}>
      {isSuccess && !shouldQualifyNewLead && (
        <Navigate
          to={
            dossier && destinationIsAllowed
              ? `${getScopedPageUrl(
                  destination as PageDirectoryEntryName,
                  dossier.id,
                  secondaryId
                )}${otherQueryString ? `?${otherQueryString}` : ""}`
              : getScopedPageUrl("home")
          }
          replace={true}
        />
      )}
    </PageContainer>
  );
};

export default RedirectToLatestDossierPage;
